import { useEffect, useRef } from "react";
import { saveCardDraft } from "../utils/api";
import fbPixel from "../utils/fbPixel";

/**
 * Auto-saves a greeting-card draft.
 *
 * @param {object} cardState  – everything the backend needs
 * @param {string|null} cardId – null for a brand-new card, or an existing id
 * @param {(id:string)=>void} onId      – called exactly once when the first id comes back
 * @param {boolean} enabled   – optional flag to turn autosave on/off (default true)
 * @param {()=>void} onSaveComplete – called after each successful auto-save
 */
export function useAutoSaveCard(
  cardState,
  cardId,
  onId,
  enabled = true,
  onSaveComplete = null,
) {
  const idRef = useRef(cardId);
  const timerRef = useRef();
  const lastSaveRef = useRef(null);
  const initialSaveRef = useRef(false);
  const hasRealIdRef = useRef(false); // Track if we have a real Firestore ID

  // Initialize hasRealIdRef based on cardId
  useEffect(() => {
    // Consider it a real ID if it exists and doesn't start with "ai_"
    hasRealIdRef.current = cardId && !cardId.startsWith("ai_");
    idRef.current = hasRealIdRef.current ? cardId : null;
  }, [cardId]);

  useEffect(() => {
    if (!enabled) return;

    // Skip initial empty save
    if (!initialSaveRef.current && !cardState.front_text?.trim() && !cardState.inside_text?.trim()) {
      return;
    }

    // Prevent too frequent saves
    const now = Date.now();
    const timeSinceLastSave = lastSaveRef.current ? now - lastSaveRef.current : 1000;
    
    // For image URL changes (AI operations), allow faster saves (500ms instead of 1000ms)
    const isImageUrlChange = cardState.image_url && cardState.image_url.includes('/uploads/');
    const minDelay = isImageUrlChange ? 500 : 1000;
    
    if (lastSaveRef.current && timeSinceLastSave < minDelay) {
      console.log(`[Auto-save] Rate limited - waiting ${minDelay - timeSinceLastSave}ms`, {
        isImageUrlChange,
        timeSinceLastSave,
        minDelay
      });
      return;
    }

    // debounce
    clearTimeout(timerRef.current);
    
    // Reduce debounce delay for image URL changes (AI operations)
    const debounceDelay = isImageUrlChange ? 1000 : 2000;
    
    timerRef.current = setTimeout(async () => {
      // ───────────── build payload ─────────────
      // Only include card_id if we have a real Firestore ID (not AI-generated temp ID)
      const payload = {
        ...(hasRealIdRef.current && idRef.current ? { card_id: idRef.current } : {}),
        title: String(cardState.front_text || "").slice(0, 30) || "Untitled Card",
        ...cardState,
        layout: {
          ...cardState.layout,
          // Keep x/y as undefined if not set - this allows auto-centering on reload
          x: typeof cardState.layout?.x === 'number' ? Math.round(cardState.layout.x) : undefined,
          y: typeof cardState.layout?.y === 'number' ? Math.round(cardState.layout.y) : undefined,
          width: Math.round(cardState.layout?.width || 0),
          height: Math.round(cardState.layout?.height || 0),
          rotation: typeof cardState.layout?.rotation === 'number' ? cardState.layout.rotation : 0,
          scaleX: typeof cardState.layout?.scaleX === 'number' ? cardState.layout.scaleX : 1,
          scaleY: typeof cardState.layout?.scaleY === 'number' ? cardState.layout.scaleY : 1,
          padding: cardState.layout?.padding || 0,
          align: cardState.layout?.align || "center",
          fontSize: cardState.layout?.fontSize || 72,
          fontFamily: cardState.layout?.fontFamily || "Lilita One",
          fontWeight: cardState.layout?.fontWeight || 700,
          lineHeight: cardState.layout?.lineHeight || 1.2,
          letterSpacing: cardState.layout?.letterSpacing || 1,
          fill: cardState.layout?.fill || "#000000",
          stroke: cardState.layout?.stroke || "",
          strokeWidth: cardState.layout?.strokeWidth || 0,
          animStyle: cardState.layout?.animStyle || "none",
          text: cardState.layout?.text || cardState.front_text || "",
          transform: cardState.layout?.transform || "none",
          // Cover border/frame settings
          borderColor: cardState.layout?.borderColor || "",
          borderWidth: cardState.layout?.borderWidth || 0,
          borderStyle: cardState.layout?.borderStyle || "solid",
          // Cover overlay settings
          overlay: cardState.layout?.overlay || "none",
          overlayColor: cardState.layout?.overlayColor || "#1F2937"
        },
        inside_layout: {
          ...cardState.inside_layout,
          x: typeof cardState.inside_layout?.x === 'number' ? Math.round(cardState.inside_layout.x) : 40,
          y: typeof cardState.inside_layout?.y === 'number' ? Math.round(cardState.inside_layout.y) : 60,
          width: Math.round(cardState.inside_layout?.width || 0),
          height: Math.round(cardState.inside_layout?.height || 0),
          rotation: typeof cardState.inside_layout?.rotation === 'number' ? cardState.inside_layout.rotation : 0,
          scaleX: typeof cardState.inside_layout?.scaleX === 'number' ? cardState.inside_layout.scaleX : 1,
          scaleY: typeof cardState.inside_layout?.scaleY === 'number' ? cardState.inside_layout.scaleY : 1,
          align: cardState.inside_layout?.align || "left",
          // Use inside_style for font properties as they're the source of truth
          fontFamily: cardState.inside_style?.fontFamily || "'Inter', sans-serif",
          fontSize: cardState.inside_style?.fontSize || 24,
          fill: cardState.inside_style?.fill || "#000000",
          stroke: cardState.inside_layout?.stroke || "",
          strokeWidth: cardState.inside_layout?.strokeWidth || 0,
          animStyle: cardState.inside_layout?.animStyle || "none",
          text: cardState.inside_layout?.text || ""
        },
        inside_style: {
          ...cardState.inside_style,
          fontFamily: cardState.inside_style?.fontFamily || "'Inter', sans-serif",
          fontSize: cardState.inside_style?.fontSize || 24,
          fill: cardState.inside_style?.fill || "#000000",
          // Inside card border settings
          borderColor: cardState.inside_style?.borderColor || "",
          borderWidth: cardState.inside_style?.borderWidth || 0,
          borderStyle: cardState.inside_style?.borderStyle || "solid"
        },
        signatures: Array.isArray(cardState.signatures) 
          ? cardState.signatures.map((s) => String(s || "").trim()).filter(Boolean)
          : [],
        // Save card dimensions for proper restoration
        card_size: cardState.card_size || "",
        // Only set status to draft for new cards
        status: hasRealIdRef.current ? undefined : "draft",
      };

      try {
        lastSaveRef.current = now;
        initialSaveRef.current = true;
        const newId = await saveCardDraft(payload);
        
        // If this was a new card (no real ID), update our refs and notify parent
        if (!hasRealIdRef.current) {
          hasRealIdRef.current = true;
          idRef.current = newId;
          onId(newId);
          
          // Track card creation event on first save
          fbPixel.trackCardCreated('custom');
        }
        
        // Notify parent component that auto-save completed
        if (onSaveComplete) {
          onSaveComplete();
        }
      } catch (err) {
        // Handle limit errors (402 = payment required, 403 = forbidden/limit exceeded)
        if (err.response?.status === 402 || err.response?.status === 403) {
          const errorDetail = err.response?.data?.detail;
          let errorMessage = "You need an active subscription to save this card. Please subscribe first.";
          let requiresUpgrade = true;
          
          if (typeof errorDetail === 'string') {
            errorMessage = errorDetail;
          } else if (typeof errorDetail === 'object' && errorDetail?.message) {
            errorMessage = errorDetail.message;
            requiresUpgrade = errorDetail.upgrade_required !== false;
          }
          
          // Dispatch a custom event for the ErrorContext to show a proper modal
          const errorEvent = new CustomEvent('showError', { 
            detail: { message: errorMessage, requiresUpgrade } 
          });
          window.dispatchEvent(errorEvent);
        } else {
          console.error("Auto-save error:", err);
        }
      }
    }, debounceDelay);

    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    cardState.front_text,
    cardState.inside_text,
    cardState.image_url,
    cardState.video_url,      // NEW: Watch video URL for video greeting cards
    cardState.media_type,     // NEW: Watch media type changes
    // Watch all layout properties
    cardState.layout?.x,
    cardState.layout?.y,
    cardState.layout?.width,
    cardState.layout?.height,
    cardState.layout?.rotation,
    cardState.layout?.scaleX,
    cardState.layout?.scaleY,
    cardState.layout?.fontSize,
    cardState.layout?.fontFamily,
    cardState.layout?.fill,
    cardState.layout?.stroke,
    cardState.layout?.strokeWidth,
    cardState.layout?.align,
    cardState.layout?.animStyle,
    // Watch cover border/overlay properties
    cardState.layout?.borderColor,
    cardState.layout?.borderWidth,
    cardState.layout?.borderStyle,
    cardState.layout?.overlay,
    cardState.layout?.overlayColor,
    // Watch all inside layout properties
    cardState.inside_layout?.x,
    cardState.inside_layout?.y,
    cardState.inside_layout?.width,
    cardState.inside_layout?.height,
    cardState.inside_layout?.rotation,
    cardState.inside_layout?.scaleX,
    cardState.inside_layout?.scaleY,
    cardState.inside_layout?.fontSize,
    cardState.inside_layout?.fontFamily,
    cardState.inside_layout?.fill,
    cardState.inside_layout?.stroke,
    cardState.inside_layout?.strokeWidth,
    cardState.inside_layout?.align,
    cardState.inside_layout?.animStyle,
    cardState.inside_layout?.text,
    // Watch inside style properties (borders)
    cardState.inside_style?.fontFamily,
    cardState.inside_style?.fontSize,
    cardState.inside_style?.fill,
    cardState.inside_style?.borderColor,
    cardState.inside_style?.borderWidth,
    cardState.inside_style?.borderStyle,
    // Watch card dimensions
    cardState.card_size,
    // Watch other state (signatures stringified for deep comparison)
    cardState.signatures,
  ]);
}
